/**
 * Overbalance (Gann) — typy odpowiedzi `/market/overbalance` i `/market/overbalance/scan`
 * oraz czyste funkcje widoku. Silnik liczy backend (`common/src/overbalance.rs`); tu tylko
 * stany, progi i zdania do pokazania. Projekt: docs/OVERBALANCE-MODULE.md.
 */

export type Rama = '1h' | '4h' | '1d';
export const RAMY: Rama[] = ['1h', '4h', '1d'];
export const SEKUNDY_RAMY: Record<Rama, number> = { '1h': 3600, '4h': 14_400, '1d': 86_400 };

export type Rodzaj = 'cena' | 'czas' | 'oba';
export type Kierunek = 'up' | 'down';

/** Poniżej tylu przypadków nie podajemy szansy (jak w Zakresie dnia). */
export const MIN_PRZYPADKOW = 30;
/** „Blisko rekordu” od tylu procent rekordu. */
export const PROG_BLISKO = 0.85;

export interface KorektaWidok {
	od_t: number;
	od: number;
	do_t: number;
	do_ceny: number;
	glebokosc: number;
	swiec: number;
}

export interface BiezacaWidok {
	od_t: number;
	od: number;
	swiec: number;
	glebokosc: number;
	max_glebokosc: number;
	prog_ceny: number | null;
	prog_czasu_t: number | null;
	ob_cena_t: number | null;
	ob_czas_t: number | null;
	zuzycie_ceny: number | null;
	zuzycie_czasu: number | null;
	aktywna: boolean;
}

export interface TrendWidok {
	kierunek: Kierunek;
	od_t: number;
	poczatek_t: number;
	poczatek: number;
	swiec: number;
	korekty: KorektaWidok[];
	rekord_glebokosc: number | null;
	rekord_swiec: number | null;
	biezaca: BiezacaWidok | null;
	/** Ostatni potwierdzony dołek (szczyt w spadkowym): zamknięcie za nim kończy trend. */
	prog_konca: number | null;
}

export interface PivotWidok {
	t: number;
	cena: number;
	szczyt: boolean;
}

export interface ZdarzenieWidok {
	t: number;
	kierunek: Kierunek;
	rodzaj: Rodzaj;
	cena: number;
	rekord: number;
	koniec: boolean | null;
	wynik_t: number | null;
	/** Strefa korekty: szczyt (dołek w spadkowym) → ostatnia świeca korekty. */
	od_t: number;
	od: number;
	do_t: number;
	/** Najdalsza cena korekty i cena progu rekordu ceny. */
	ekstremum: number;
	prog: number;
	max_glebokosc: number;
}

export interface StatRodzaju {
	rodzaj: Rodzaj;
	n: number;
	koniec: number | null;
	blizniaki: number | null;
}

export interface Analiza {
	interwal: number;
	ostatnia_t: number;
	cena: number;
	trend: TrendWidok | null;
	pivoty: PivotWidok[];
	zdarzenia: ZdarzenieWidok[];
	statystyka: StatRodzaju[];
}

/** Punkt krzywej z pomiaru: ułamek rekordu (1 = rekord), liczba korekt, % końca trendu. */
export interface PunktKrzywej {
	zuzycie: number;
	n: number;
	koniec: number;
}

/** [czas, open, high, low, close, wolumen] — wolumen od 30.10 (starsze odpowiedzi bez niego). */
export type SwiecaOb = [number, number, number, number, number, number?];

export interface OverbalanceResponse {
	symbol: string;
	analiza: Analiza;
	/** Świece od najstarszej. */
	swiece: SwiecaOb[];
	/** Tylko przy analizie „na moment” (`?do=`): świece po tym momencie — co było potem. */
	po?: SwiecaOb[];
	/** Pomiar z Fazy 0 (25 płynnych rynków) dla tej ramy. */
	zbiorcze: StatRodzaju[];
	/** Szansa końca trendu, gdy korekta pierwszy raz sięga ułamka rekordu ceny / czasu. */
	krzywa_ceny: PunktKrzywej[];
	krzywa_czasu: PunktKrzywej[];
}

export interface OverbalanceScanRow {
	symbol: string;
	cena: number;
	obrot: number | null;
	trend: TrendWidok | null;
}

export interface OverbalanceScanResponse {
	interwal: number;
	generated_at: number;
	wiersze: OverbalanceScanRow[];
}

/**
 * Stan bieżącej korekty:
 * - `zbyt_wczesnie` — trend ma mniej niż 2 zamknięte korekty, rekord jeszcze nic nie znaczy,
 * - `daleko` / `blisko` — przed rekordem (blisko = ≥ 85% rekordu ceny albo czasu),
 * - `cena` / `czas` / `oba` — rekord pobity.
 */
export type StanKorekty = 'brak' | 'zbyt_wczesnie' | 'daleko' | 'blisko' | Rodzaj;

export function stanKorekty(t: TrendWidok | null): StanKorekty {
	const b = t?.biezaca;

	if (!b) {
		return 'brak';
	}

	if (!b.aktywna) {
		return 'zbyt_wczesnie';
	}

	if (b.ob_cena_t !== null && b.ob_czas_t !== null) {
		return 'oba';
	}

	if (b.ob_cena_t !== null) {
		return 'cena';
	}

	if (b.ob_czas_t !== null) {
		return 'czas';
	}

	return Math.max(b.zuzycie_ceny ?? 0, b.zuzycie_czasu ?? 0) >= PROG_BLISKO ? 'blisko' : 'daleko';
}

/** Rodzaj Overbalance, który już padł (do szansy z pomiaru), albo null. */
export function rodzajStanu(s: StanKorekty | StanZywy): Rodzaj | null {
	return s === 'cena' || s === 'czas' || s === 'oba' ? s : null;
}

/** Pasek zużycia rekordu: skala do 150%, kreska rekordu na 2/3 szerokości. */
export function pasekZuzycia(z: number | null): number {
	return z === null ? 0 : Math.max(0, Math.min(100, (z / 1.5) * 100));
}

export const KRESKA_REKORDU = (1 / 1.5) * 100;

/** Wiersz skanera: liczby z bieżącego trendu, gotowe do sortowania. */
export interface WierszSkanu {
	symbol: string;
	cena: number;
	obrot: number | null;
	kierunek: Kierunek | null;
	swiecTrendu: number | null;
	korekt: number;
	glebokosc: number | null;
	rekord: number | null;
	zuzycieCeny: number | null;
	zuzycieCzasu: number | null;
	/** Większe z dwóch zużyć — do sortowania „najbliżej rekordu”. */
	zuzycie: number | null;
	stan: StanZywy;
	progCeny: number | null;
	/** Skaner na żywo: stan z ceny teraz czeka na zamknięcie świecy. */
	niepotwierdzone?: boolean;
}

export function wierszeSkanu(r: OverbalanceScanResponse): WierszSkanu[] {
	return r.wiersze.map((w) => {
		const t = w.trend;
		const b = t?.biezaca ?? null;
		const zc = b?.aktywna ? b.zuzycie_ceny : null;
		const zt = b?.aktywna ? b.zuzycie_czasu : null;
		return {
			symbol: w.symbol,
			cena: w.cena,
			obrot: w.obrot,
			kierunek: t?.kierunek ?? null,
			swiecTrendu: t?.swiec ?? null,
			korekt: t?.korekty.length ?? 0,
			glebokosc: b?.glebokosc ?? null,
			rekord: t?.rekord_glebokosc ?? null,
			zuzycieCeny: zc,
			zuzycieCzasu: zt,
			zuzycie: zc === null && zt === null ? null : Math.max(zc ?? 0, zt ?? 0),
			stan: stanKorekty(t),
			progCeny: b?.aktywna ? b.prog_ceny : null,
		};
	});
}

/**
 * Wiersze skanera przeliczone z ceny na żywo (`ceny`: symbol → cena). Wiersz bez ceny albo
 * bez bieżącej korekty zostaje z zamknięcia.
 */
export function wierszeNaZywo(
	r: OverbalanceScanResponse,
	ceny: Map<string, number>,
	terazS: number,
): WierszSkanu[] {
	const trendy = new Map(r.wiersze.map((w) => [w.symbol, w.trend]));
	return wierszeSkanu(r).map((w) => {
		const c = ceny.get(w.symbol);
		const t = trendy.get(w.symbol) ?? null;
		const k = c ? korektaZCeny(t, r.interwal, c, terazS) : null;

		if (!c || !k) {
			return c ? { ...w, cena: c } : w;
		}

		const aktywna = !!t?.biezaca?.aktywna;
		const zc = aktywna ? k.zuzycieCeny : null;
		const zt = aktywna ? k.zuzycieCzasu : null;
		return {
			...w,
			cena: c,
			glebokosc: k.glebokosc,
			zuzycieCeny: zc,
			zuzycieCzasu: zt,
			zuzycie: zc === null && zt === null ? null : Math.max(zc ?? 0, zt ?? 0),
			stan: k.stan,
			niepotwierdzone: k.niepotwierdzone,
		};
	});
}

export interface PodsumowanieSkanu {
	n: number;
	wzrostowe: number;
	aktywne: number;
	blisko: number;
	pobite: number;
}

export function podsumowanieSkanu(w: WierszSkanu[]): PodsumowanieSkanu {
	return {
		n: w.length,
		wzrostowe: w.filter((x) => x.kierunek === 'up').length,
		aktywne: w.filter((x) => x.stan !== 'brak' && x.stan !== 'zbyt_wczesnie').length,
		blisko: w.filter((x) => x.stan === 'blisko').length,
		pobite: w.filter((x) => rodzajStanu(x.stan) !== null).length,
	};
}

/**
 * Wartość krzywej przy ułamku rekordu `z` (interpolacja liniowa między progami pomiaru);
 * poza zakresem pomiaru (0,5–1,5) — null, żeby nie zgadywać.
 */
export function zKrzywej(k: PunktKrzywej[], z: number | null): number | null {
	if (z === null || !k.length || z < k[0].zuzycie || z > k[k.length - 1].zuzycie) {
		return null;
	}

	const i = Math.max(
		1,
		k.findIndex((p) => p.zuzycie >= z),
	);
	const [a, b] = [k[i - 1], k[i]];
	const w = b.zuzycie > a.zuzycie ? (z - a.zuzycie) / (b.zuzycie - a.zuzycie) : 0;
	return a.koniec + (b.koniec - a.koniec) * w;
}

/**
 * Strefa Overbalance na wykresie: jedna korekta, która pobiła rekord trendu (cenowy, czasowy
 * albo oba — zdarzenia tej samej korekty sklejone w jedną strefę).
 */
export interface StrefaOb {
	kierunek: Kierunek;
	rodzaje: Rodzaj[];
	/** Pierwsze zdarzenie w korekcie (unix s) i cena zamknięcia tej świecy. */
	t: number;
	cena: number;
	od_t: number;
	do_t: number;
	od: number;
	ekstremum: number;
	prog: number;
	/** Rekord głębokości (%) i największa głębokość korekty (%). */
	rekord: number | null;
	max_glebokosc: number;
	koniec: boolean | null;
	wynik_t: number | null;
}

export function strefy(zdarzenia: ZdarzenieWidok[]): StrefaOb[] {
	const m = new Map<string, StrefaOb>();

	for (const z of [...zdarzenia].sort((a, b) => a.t - b.t)) {
		const k = `${z.kierunek}:${z.od_t}`;
		const s = m.get(k);

		if (s) {
			if (!s.rodzaje.includes(z.rodzaj)) {
				s.rodzaje.push(z.rodzaj);
			}

			if (s.rekord === null && z.rodzaj !== 'czas') {
				s.rekord = z.rekord;
			}

			continue;
		}

		m.set(k, {
			kierunek: z.kierunek,
			rodzaje: [z.rodzaj],
			t: z.t,
			cena: z.cena,
			od_t: z.od_t,
			do_t: z.do_t,
			od: z.od,
			ekstremum: z.ekstremum,
			prog: z.prog,
			rekord: z.rodzaj === 'czas' ? null : z.rekord,
			max_glebokosc: z.max_glebokosc,
			koniec: z.koniec,
			wynik_t: z.wynik_t,
		});
	}

	return [...m.values()];
}

/** Statystyka rodzaju z listy (ta krypto albo pomiar zbiorczy). */
export function stat(lista: StatRodzaju[], r: Rodzaj): StatRodzaju | null {
	return lista.find((s) => s.rodzaj === r) ?? null;
}

/**
 * Ile jeszcze świec musi się zamknąć do rekordu czasu (1 = najbliższe zamknięcie).
 * `prog_czasu_t` to POCZĄTEK świecy, której zamknięcie pobije rekord; `ostatnia` — początek
 * ostatniej zamkniętej.
 */
export function swiecDoProgu(b: BiezacaWidok, interwal: number, ostatnia: number): number | null {
	if (b.prog_czasu_t === null) {
		return null;
	}

	return Math.max(0, Math.round((b.prog_czasu_t - ostatnia) / interwal));
}

/** Moment zamknięcia świecy zaczynającej się o `t`. */
export function zamkniecie(t: number, interwal: number): number {
	return t + interwal;
}

/** Świeca w toku: początek (unix s) i OHLC — z API świec, potem z cen na żywo. */
export interface SwiecaZywa {
	t: number;
	o: number;
	h: number;
	l: number;
	c: number;
	/** Wolumen z API świec (ceny na żywo go nie aktualizują). */
	v?: number;
}

/** Stan na żywo: jak `StanKorekty` + nowy szczyt korekty i zamknięcie, które skończy trend. */
export type StanZywy = StanKorekty | 'nowy_szczyt' | 'koniec_trendu';

/**
 * Bieżąca korekta policzona z ceny teraz (w trakcie świecy). Silnik liczy rekord na
 * zamknięciu — to, co tu przekracza próg, jest „niepotwierdzone” do zamknięcia świecy.
 */
export interface KorektaZywa {
	cena: number;
	glebokosc: number;
	zuzycieCeny: number | null;
	/** Z ułamkiem trwającej świecy (np. 4,6 / 5 świec rekordu). */
	zuzycieCzasu: number | null;
	/** Cena za progiem rekordu ceny w trakcie świecy (rekord jeszcze nie pobity na zamknięciu). */
	przebitaCena: boolean;
	/** Zamknięcie tej świecy pobije rekord czasu. */
	przebityCzas: boolean;
	/** Świeca wyszła ponad szczyt korekty (pod dołek w spadkowym) — korekta zacznie się od nowa. */
	nowySzczyt: boolean;
	/** Cena za ostatnim dołkiem (szczytem) — zamknięcie tu skończy trend. */
	zagrozenieKonca: boolean;
	stan: StanZywy;
	/** Coś z powyższego czeka na zamknięcie świecy. */
	niepotwierdzone: boolean;
	/** Sekundy do zamknięcia świecy. */
	doZamkniecia: number;
}

/**
 * Korekta na żywo z trendu (stan z ostatniego zamknięcia) i świecy w toku. Null, gdy świeca
 * w toku nie jest następną po ostatniej zamkniętej (analiza czeka na świeżą świecę) albo nie
 * ma bieżącej korekty.
 */
export function korektaNaZywo(
	t: TrendWidok | null,
	interwal: number,
	ostatnia: number,
	sw: SwiecaZywa,
	terazS: number,
): KorektaZywa | null {
	const b = t?.biezaca;

	if (!t || !b || sw.t !== ostatnia + interwal || !(sw.c > 0) || !(b.od > 0)) {
		return null;
	}

	const up = t.kierunek === 'up';
	const nowySzczyt = up ? sw.h > b.od : sw.l < b.od;
	// Nowy szczyt w trakcie świecy: korekta liczy się od niego (jak silnik na zamknięciu).
	const szczyt = nowySzczyt ? (up ? sw.h : sw.l) : b.od;
	const glebokosc = Math.max(0, ((up ? szczyt - sw.c : sw.c - szczyt) / szczyt) * 100);
	const rekordG = b.prog_ceny !== null ? (Math.abs(b.od - b.prog_ceny) / b.od) * 100 : null;
	const md = b.prog_czasu_t !== null ? (b.prog_czasu_t - b.od_t) / interwal - 1 : null;
	const ulamek = Math.min(1, Math.max(0, (terazS - sw.t) / interwal));
	const zuzycieCeny = rekordG ? glebokosc / rekordG : null;
	const zuzycieCzasu = md ? (nowySzczyt ? ulamek : b.swiec + ulamek) / md : null;
	const przebitaCena =
		!nowySzczyt &&
		b.aktywna &&
		b.ob_cena_t === null &&
		b.prog_ceny !== null &&
		(up ? sw.c < b.prog_ceny : sw.c > b.prog_ceny);
	const przebityCzas =
		!nowySzczyt &&
		b.aktywna &&
		b.ob_czas_t === null &&
		b.prog_czasu_t !== null &&
		sw.t >= b.prog_czasu_t;
	const zagrozenieKonca =
		t.prog_konca !== null && (up ? sw.c < t.prog_konca : sw.c > t.prog_konca);

	let stan: StanZywy;

	if (zagrozenieKonca) {
		stan = 'koniec_trendu';
	} else if (nowySzczyt) {
		stan = 'nowy_szczyt';
	} else if (!b.aktywna) {
		stan = 'zbyt_wczesnie';
	} else {
		const c = b.ob_cena_t !== null || przebitaCena;
		const cz = b.ob_czas_t !== null || przebityCzas;
		stan =
			c && cz
				? 'oba'
				: c
				? 'cena'
				: cz
				? 'czas'
				: Math.max(zuzycieCeny ?? 0, zuzycieCzasu ?? 0) >= PROG_BLISKO
				? 'blisko'
				: 'daleko';
	}

	return {
		cena: sw.c,
		glebokosc,
		zuzycieCeny,
		zuzycieCzasu,
		przebitaCena,
		przebityCzas,
		nowySzczyt,
		zagrozenieKonca,
		stan,
		niepotwierdzone: przebitaCena || przebityCzas || nowySzczyt || zagrozenieKonca,
		doZamkniecia: Math.max(0, sw.t + interwal - terazS),
	};
}

/**
 * Korekta na żywo z samej ceny (skaner i pomiar: bez świecy w toku). Świeca w toku = ta,
 * która trwa teraz; jako ostatnią zamkniętą przyjmujemy poprzednią.
 */
export function korektaZCeny(
	t: TrendWidok | null,
	interwal: number,
	cena: number,
	terazS: number,
): KorektaZywa | null {
	const start = terazS - (terazS % interwal);
	return korektaNaZywo(
		t,
		interwal,
		start - interwal,
		{ t: start, o: cena, h: cena, l: cena, c: cena },
		terazS,
	);
}

/** „1 h 05 min”, „12 min 04 s” — czas do zamknięcia świecy / od startu pomiaru. */
export function czasTrwania(s: number): string {
	const x = Math.max(0, Math.floor(s));
	const d = Math.floor(x / 86_400);
	const h = Math.floor((x % 86_400) / 3600);
	const m = Math.floor((x % 3600) / 60);
	const sek = x % 60;
	const dwa = (n: number): string => String(n).padStart(2, '0');

	if (d) {
		return `${d} d ${h} h`;
	}

	if (h) {
		return `${h} h ${dwa(m)} min`;
	}

	return `${m} min ${dwa(sek)} s`;
}

/** Filtry historii zdarzeń. */
export type FiltrWyniku = 'wszystkie' | 'koniec' | 'trwal' | 'otwarte';

export function filtrujZdarzenia(
	z: ZdarzenieWidok[],
	rodzaj: Rodzaj | 'wszystkie',
	kierunek: Kierunek | 'wszystkie',
	wynik: FiltrWyniku,
): ZdarzenieWidok[] {
	return z.filter(
		(x) =>
			(rodzaj === 'wszystkie' || x.rodzaj === rodzaj) &&
			(kierunek === 'wszystkie' || x.kierunek === kierunek) &&
			(wynik === 'wszystkie' ||
				(wynik === 'otwarte'
					? x.koniec === null
					: wynik === 'koniec'
					? x.koniec === true
					: x.koniec === false)),
	);
}
